import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, DollarSign, TrendingUp, Users, CheckCircle2, ChevronDown } from "lucide-react";
import { API } from "../../lib/adventureApi";
import { authHeader } from "../../lib/auth";

const STATUS_CLS = {
  secured: "bg-amber-100 text-amber-800", payable: "bg-blue-100 text-blue-800",
  paid: "bg-moss-light text-moss", on_hold: "bg-ink/10 text-ink-secondary",
  reversed: "bg-terracotta/15 text-terracotta", cancelled: "bg-ink/10 text-ink-muted",
};
const NEXT = { secured: ["payable", "on_hold", "reversed"], payable: ["paid", "on_hold"], on_hold: ["payable", "reversed"], paid: [], reversed: [], cancelled: [] };

function Card({ label, value, icon: Icon, money }) {
  return (
    <div className="rounded-xl bg-white border border-sand-dark p-4" data-testid={`report-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="flex items-center gap-2 text-ink-muted"><Icon className="w-4 h-4" /><p className="font-mono text-[10px] uppercase tracking-widest">{label}</p></div>
      <p className="font-display font-extrabold text-2xl text-ink mt-1 tabular-nums">{money ? `A$${Number(value).toLocaleString()}` : value}</p>
    </div>
  );
}

function CommRow({ c, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const setStatus = async (status) => {
    setBusy(true); setOpen(false);
    try { const r = await fetch(`${API}/admin/mpn/commissions/${c.id}`, { method: "PATCH", headers: { ...authHeader(), "Content-Type": "application/json" }, body: JSON.stringify({ status }) }); if (!r.ok) throw new Error(); toast.success(`Marked ${status}`); onChange(); }
    catch { toast.error("Update failed"); } finally { setBusy(false); }
  };
  return (
    <tr className="border-t border-sand" data-testid={`admin-comm-${c.id}`}>
      <td className="px-3 py-2 font-mono text-xs">{c.booking_ref}</td>
      <td className="px-3 py-2">{c.partner_id}</td>
      <td className="px-3 py-2 text-ink-secondary">{c.campaign_code}</td>
      <td className="px-3 py-2">T{c.tranche} · {c.basis}</td>
      <td className="px-3 py-2 text-right tabular-nums font-semibold text-terracotta">A${Number(c.amount).toLocaleString()}</td>
      <td className="px-3 py-2"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[c.status]}`} data-testid={`admin-comm-status-${c.id}`}>{c.status}</span></td>
      <td className="px-3 py-2 text-right relative">
        {NEXT[c.status]?.length ? (
          <>
            <button onClick={() => setOpen((o) => !o)} onBlur={() => setTimeout(() => setOpen(false), 150)} disabled={busy} data-testid={`admin-comm-manage-${c.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-sm border border-sand-dark text-xs font-semibold hover:border-moss disabled:opacity-40">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Set <ChevronDown className="w-3 h-3" /></>}</button>
            {open && <div className="absolute right-2 top-10 w-32 bg-white border border-sand-dark rounded-sm shadow-lg z-10 overflow-hidden">{NEXT[c.status].map((s) => <button key={s} onMouseDown={() => setStatus(s)} data-testid={`admin-comm-set-${s}-${c.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-sand capitalize">{s}</button>)}</div>}
          </>
        ) : <span className="text-ink-muted text-xs">—</span>}
      </td>
    </tr>
  );
}

export default function CommissionsAdmin() {
  const [report, setReport] = useState(null);
  const [comms, setComms] = useState(null);
  const load = () => {
    fetch(`${API}/admin/mpn/reports`, { headers: authHeader() }).then((r) => r.json()).then(setReport).catch(() => setReport(null));
    fetch(`${API}/admin/mpn/commissions`, { headers: authHeader() }).then((r) => r.json()).then(setComms).catch(() => setComms([]));
  };
  useEffect(() => { load(); }, []);
  const t = report?.totals || {};

  return (
    <div data-testid="commissions-admin" className="space-y-5">
      <div className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-terracotta" /><h2 className="font-display font-extrabold text-lg">Commissions & Reports</h2></div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Leads" value={t.leads || 0} icon={Users} />
        <Card label="Deposits paid" value={t.deposits_paid || 0} icon={CheckCircle2} />
        <Card label="Deposit conversion" value={`${t.deposit_conversion || 0}%`} icon={TrendingUp} />
        <Card label="Confirmed" value={t.confirmed || 0} icon={CheckCircle2} />
        <Card label="Commission liability" value={t.commission_liability || 0} icon={DollarSign} money />
        <Card label="Commission paid" value={t.commission_paid || 0} icon={DollarSign} money />
      </div>

      {report && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl bg-white border border-sand-dark p-4" data-testid="report-by-campaign">
            <p className="font-display font-bold mb-2">Leads by campaign</p>
            {Object.entries(report.leads_by_campaign).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-0.5 border-b border-sand last:border-0"><span className="text-ink-secondary">{k}</span><span className="font-semibold tabular-nums">{v}</span></div>)}
          </div>
          <div className="rounded-xl bg-white border border-sand-dark p-4" data-testid="report-by-partner">
            <p className="font-display font-bold mb-2">Leads by partner</p>
            {Object.entries(report.leads_by_partner).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-0.5 border-b border-sand last:border-0"><span className="text-ink-secondary">{k}</span><span className="font-semibold tabular-nums">{v}</span></div>)}
          </div>
        </div>
      )}

      <div>
        <p className="font-display font-bold mb-2">Commission ledger</p>
        {comms == null ? <div className="text-ink-secondary flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          : comms.length === 0 ? <p className="text-sm text-ink-secondary">No commissions yet.</p>
          : (
          <div className="overflow-x-auto rounded-xl border border-sand-dark bg-white">
            <table className="w-full text-sm" data-testid="admin-commission-table">
              <thead className="bg-sand text-ink-secondary font-mono text-[10px] uppercase tracking-widest"><tr>
                <th className="text-left px-3 py-2">Booking</th><th className="text-left px-3 py-2">Partner</th><th className="text-left px-3 py-2">Campaign</th><th className="text-left px-3 py-2">Tranche</th><th className="text-right px-3 py-2">Amount</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">Action</th></tr></thead>
              <tbody>{comms.map((c) => <CommRow key={c.id} c={c} onChange={load} />)}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
